import {PerspectiveCamera} from '@react-three/drei'
import {useFrame} from '@react-three/fiber'
import {isDebugMode} from '@repo/shared/utils/isDebugMode'
import {useAtomValue} from 'jotai'
import {damp3} from 'maath/easing'
import {useRef} from 'react'
import {Group, Mesh, PerspectiveCamera as PerspectiveCameraImpl, Vector3} from 'three'
import {EScene} from '../../types/scene.types'
import {lobbyPos} from '../Lobby/Lobby.screen'
import {sceneState} from '../Store/atoms'
import FrameStore from '../Store/FrameStore'

const lobbyLookAt = new Vector3(lobbyPos.x + 0.1, lobbyPos.y, lobbyPos.z)

const fStore = new FrameStore()

const SHAKE_MULTIPLIER = 0.5

export const GameCamera = () => {
  const cameraRef = useRef<PerspectiveCameraImpl | null>(null)
  const camGroupRef = useRef<Group | null>(null)
  const lookAtRef = useRef<Mesh | null>(null)
  const isDebug = useRef(isDebugMode())

  const scene = useAtomValue(sceneState)

  useFrame(({clock}, delta) => {
    if (!cameraRef.current || !lookAtRef.current || !camGroupRef.current) return
    fStore.updateFirstAndLastTeamPlace()

    fStore.updateCameraVectors()
    const followCamVec = fStore.getFollowCamVec()
    const followCamLookatVec = fStore.getFollowCamLookatVec()

    const raceShakeYValue = Math.max(0.33, fStore.getFirstPlaceTeamSpeed() * 1.2)
    const raceShakeZValue = Math.max(0.2, fStore.getFirstPlaceTeamSpeed() * 1.2)

    // always look at the lookat object
    cameraRef.current?.lookAt(lookAtRef.current?.position)

    /**
     * Lobby
     */
    if (scene === EScene.PRE_LOBBY || scene === EScene.LOBBY) {
      damp3(camGroupRef.current.position, fStore.getInitialCameraPos(), 1.25, delta)
      damp3(lookAtRef.current.position, lobbyLookAt, 1.25, delta)
    }

    /**
     * Countdown started
     */
    if (scene === EScene.STARTED_COUNTDOWN) {
      const _camShake = new Vector3(
        cameraRef.current.position.x,
        cameraRef.current.position.y + -(Math.cos(clock.getElapsedTime() * 0.5) * SHAKE_MULTIPLIER),
        cameraRef.current.position.z + Math.sin(clock.getElapsedTime() * 0.3) * SHAKE_MULTIPLIER,
      )

      const _lookatShake = new Vector3(
        followCamLookatVec.x,
        followCamLookatVec.y + Math.cos(clock.getElapsedTime() * 0.33) * SHAKE_MULTIPLIER,
        followCamLookatVec.z + Math.sin(clock.getElapsedTime() * 0.2) * SHAKE_MULTIPLIER,
      )

      damp3(cameraRef.current.position, _camShake, 1.25, delta)
      damp3(camGroupRef.current.position, followCamVec, 1.25, delta)
      damp3(lookAtRef.current.position, _lookatShake, 1.25, delta)
    }

    /**
     * Game started
     */
    if (scene === EScene.STARTED) {
      const _camShake = new Vector3(
        cameraRef.current.position.x,
        cameraRef.current.position.y + -(Math.cos(clock.getElapsedTime() * 0.5) * SHAKE_MULTIPLIER),
        cameraRef.current.position.z + Math.sin(clock.getElapsedTime() * 0.3) * SHAKE_MULTIPLIER,
      )

      const _lookatShake = new Vector3(
        followCamLookatVec.x,
        followCamLookatVec.y +
          Math.cos(clock.getElapsedTime() * raceShakeYValue) * SHAKE_MULTIPLIER,
        followCamLookatVec.z +
          Math.sin(clock.getElapsedTime() * raceShakeZValue) * SHAKE_MULTIPLIER,
      )

      damp3(cameraRef.current.position, _camShake, 1.25, delta)
      damp3(camGroupRef.current.position, followCamVec, 0.2, delta)
      // camGroupRef.current.position.set(followCamVec.x, followCamVec.y, followCamVec.z)
      damp3(lookAtRef.current.position, _lookatShake, 0.2, delta)
      // lookAtRef.current.position.set(_lookatShake.x, _lookatShake.y, _lookatShake.z)
    }

    /**
     * Game ended
     */
    if (scene === EScene.ENDED) {
      const endCamPos = new Vector3(fStore.getPathEndVec().x - 60, 30, 0)
      const _camShake = new Vector3(
        cameraRef.current.position.x,
        cameraRef.current.position.y + -(Math.cos(clock.getElapsedTime() * 0.5) * SHAKE_MULTIPLIER),
        cameraRef.current.position.z + Math.sin(clock.getElapsedTime() * 0.3) * SHAKE_MULTIPLIER,
      )

      const _lookatShake = new Vector3(
        followCamLookatVec.x,
        followCamLookatVec.y + Math.cos(clock.getElapsedTime() * 0.33) * SHAKE_MULTIPLIER,
        followCamLookatVec.z + Math.sin(clock.getElapsedTime() * 0.2) * SHAKE_MULTIPLIER,
      )

      damp3(cameraRef.current.position, _camShake, 1.25, delta)
      damp3(camGroupRef.current.position, endCamPos, 2, delta)
      damp3(lookAtRef.current.position, _lookatShake, 2, delta)
    }

    /**
     * Scoreboard
     */
    if (scene === EScene.SCOREBOARD) {
      const _camShake = new Vector3(
        cameraRef.current.position.x,
        cameraRef.current.position.y + -(Math.cos(clock.getElapsedTime() * 0.5) * SHAKE_MULTIPLIER),
        cameraRef.current.position.z + Math.sin(clock.getElapsedTime() * 0.3) * SHAKE_MULTIPLIER,
      )

      damp3(cameraRef.current.position, _camShake, 1.25, delta)
    }
  })

  return (
    <>
      <group ref={camGroupRef} position={fStore.getInitialCameraPos()}>
        <PerspectiveCamera
          makeDefault
          ref={ref => {
            cameraRef.current = ref
          }}
          fov={45}
          near={0.1}
          far={2000}
          filmGauge={36}
          rotation={[0, 0, 0]}
        />
      </group>
      <mesh ref={lookAtRef} position={lobbyPos}>
        <boxGeometry args={isDebug.current ? [0.5, 0.5, 0.5] : [0.01, 0.01, 0.01]} />
        <meshBasicMaterial color="red" />
      </mesh>
    </>
  )
}
